import React, { useEffect, useState, useRef } from "react";
import { H1, Text } from "../../../../components/UI/generalLayout/Layout.styled";
import { Spacer } from "../../../../components/UI/spacer/spacer";
import { TermoService } from "../../../../core/http/service/TermoService";
import { CircularProgress, LinearProgress } from "@mui/material";
import Colors from "../../../../constants/Colors";
import moment from "moment";
import { ReactComponent as UploadIcon } from "../../../../assets/imgs/upload.svg";
import DownloadIcon from '@mui/icons-material/Download';
import useResponsiveInfo from "../../../../hooks/useResponsiveInfo";
import GenericModal from "../../../../components/genericModal/GenericModal";
import { ReactComponent as CheckPositive } from "../../../../assets/imgs/checkPositive.svg";

interface ITermosTab {
    pessoa: any;
    agendamento?: any;
}

const TermosTab = ({ pessoa, agendamento }: ITermosTab) => {
    const [termos, setTermos] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [downloadingTermoId, setDownloadingTermoId] = useState<string | null>(null);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [errorMessage, setErrorMessage] = useState("");
    const { isMobile } = useResponsiveInfo();
    const service = new TermoService();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const targetPessoaId = pessoa?.idPessoa || pessoa?.id;
    const isFromPainel = !!agendamento && (!!agendamento.id || !!agendamento.idPessoa || !!agendamento.horario || !!agendamento.tipoConsulta);

    const fetchTermos = () => {
        if (!targetPessoaId) return;
        setLoading(true);
        service.listarTermos(targetPessoaId)
            .then(({ data: { data } }: any) => {
                setTermos(data);
            })
            .catch(() => {
                setTermos([]);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchTermos();
    }, [targetPessoaId]);

    const handleDownload = (termo: any) => {
        setDownloadingTermoId(termo.id);
        service.downloadTermo(termo.id)
            .then((response: any) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', termo.nome);
                document.body.appendChild(link);
                link.click();
                link.remove();
            })
            .finally(() => setDownloadingTermoId(null));
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            setShowConfirmModal(true);
        }
    };

    const confirmUpload = () => {
        if (selectedFile && targetPessoaId) {
            setShowConfirmModal(false);
            setIsUploading(true);
            setUploadProgress(0);
            service.uploadTermo(targetPessoaId, selectedFile, (progress) => {
                setUploadProgress(progress);
            })
                .then(() => {
                    setShowSuccessModal(true);
                    fetchTermos();
                })
                .catch((error: any) => {
                    setErrorMessage(error.response?.data?.message || "Erro ao fazer upload do termo.");
                    setShowErrorModal(true);
                })
                .finally(() => {
                    setIsUploading(false);
                    setUploadProgress(0);
                    setSelectedFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                });
        }
    };

    const cancelUpload = () => {
        setShowConfirmModal(false);
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    return (
        <div style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <H1>Termos</H1>
            </div>
            
            <Spacer top={20} />
            
            {isFromPainel && (
                <>
                    <input
                        type="file"
                        id="upload-termo"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                        accept=".pdf"
                        disabled={isUploading}
                    />
                    <label htmlFor="upload-termo" style={{ cursor: isUploading ? 'default' : 'pointer', width: '100%' }}>
                        <div style={{
                            width: '100%',
                            height: '100px',
                            border: `2px dashed ${isUploading ? Colors.GRAY300 : Colors.RED500}`,
                            borderRadius: '8px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(239, 68, 68, 0.05)',
                            padding: '0 20px',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            {isUploading ? (
                                <div style={{ width: '100%', textAlign: 'center', padding: '0 40px' }}>
                                    <Text bold style={{ color: Colors.SPM600, marginBottom: '10px' }}>
                                        Fazendo upload... {uploadProgress}%
                                    </Text>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={uploadProgress} 
                                        style={{ width: '100%', height: '10px', borderRadius: '5px' }}
                                    />
                                </div>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <UploadIcon style={{ marginRight: '15px' }} />
                                    <Text style={{ color: Colors.RED500, fontWeight: 'bold' }}>
                                        Clique aqui para adicionar o arquivo .pdf!
                                    </Text>
                                </div>
                            )}
                        </div>
                    </label>

                    <Spacer top={40} />
                </>
            )}

            {/* Table Header */}
            <div style={{ 
                display: 'flex', 
                padding: '10px 15px', 
                borderBottom: `1px solid ${Colors.GRAY200}`,
                marginBottom: '10px'
            }}>
                <Text bold style={{ flex: 3, color: Colors.GRAY800 }}>Nome</Text>
                <Text bold style={{ flex: 1, color: Colors.GRAY800, textAlign: 'center' }}>Data</Text>
                <div style={{ width: '40px' }} /> {/* Spacer for download icon */}
            </div>

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                    <CircularProgress />
                </div>
            ) : termos.length > 0 ? (
                termos.map((termo) => (
                    <div key={termo.id} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        padding: '15px', 
                        borderBottom: `1px solid ${Colors.GRAY100}`,
                    }}>
                        <Text style={{ flex: 3, color: Colors.GRAY700 }}>{termo.nome}</Text>
                        <Text style={{ flex: 1, textAlign: 'center', color: Colors.GRAY700 }}>
                            {moment(termo.dataInclusao).format('DD/MM/YYYY')}
                        </Text>
                        <div style={{ width: '40px', display: 'flex', justifyContent: 'flex-end' }}>
                            {downloadingTermoId === termo.id ? (
                                <CircularProgress size={24} style={{ color: Colors.SPM600 }} />
                            ) : (
                                <DownloadIcon 
                                    style={{ cursor: 'pointer', color: Colors.SPM600, fontSize: 24 }} 
                                    onClick={() => handleDownload(termo)} 
                                />
                            )}
                        </div>
                    </div>
                ))
            ) : (
                <div style={{ padding: '20px' }}>
                    <Text>Nenhum termo encontrado</Text>
                </div>
            )}

            {/* Modal de Confirmação antes do Upload */}
            <GenericModal
                visible={showConfirmModal}
                Icon={() => <UploadIcon style={{ width: '48px', height: '48px', color: Colors.SPM600, marginBottom: '20px' }} />}
                modalTitle="Confirmar Upload"
                modalSubtitle={`Deseja enviar o arquivo "${selectedFile?.name}"?`}
                confirmButtonMsg="Enviar"
                cancelButtonMsg="Cancelar"
                onConfirm={cancelUpload} // cancelButtonMsg "Cancelar" triggers onConfirm
                onCancel={confirmUpload}  // confirmButtonMsg "Enviar" triggers onCancel
                colorButtonConfirm={Colors.success}
            />

            {/* Modal de Sucesso */}
            <GenericModal
                visible={showSuccessModal}
                Icon={CheckPositive}
                modalTitle="Sucesso!"
                modalSubtitle="O termo foi enviado e salvo com sucesso."
                confirmButtonMsg="Fechar"
                cancelButtonMsg=""
                onCancel={() => setShowSuccessModal(false)}
                onConfirm={() => setShowSuccessModal(false)}
                colorButtonConfirm={Colors.SPM600}
            />

            {/* Modal de Erro */}
            <GenericModal
                visible={showErrorModal}
                modalTitle="Erro no Upload"
                modalSubtitle={errorMessage}
                confirmButtonMsg="Fechar"
                cancelButtonMsg=""
                onCancel={() => setShowErrorModal(false)}
                onConfirm={() => setShowErrorModal(false)}
            />
        </div>
    );
};

export default TermosTab;
